import { forwardRef, type InputHTMLAttributes } from 'react';
import { Input } from './Input';
import { Icons } from '@admin/shared/components/Icons';

interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  onClear?: () => void;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ onClear, value, onChange, className = '', ...props }, ref) => {
    const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (onClear) {
        onClear();
        return;
      }
      // Fallback: synthesize an onChange with empty value so consumers that
      // only wire onChange still get cleared.
      if (onChange) {
        const synthetic = {
          target: { value: '' },
          currentTarget: { value: '' },
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        onChange(synthetic);
      }
    };

    return (
      <Input
        ref={ref}
        type="text"
        leadingIcon={<Icons.Search className="w-4 h-4" />}
        trailingIcon={value ? (
          <button
            type="button"
            aria-label="Clear search"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            className="pointer-events-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <Icons.X className="w-3.5 h-3.5" />
          </button>
        ) : undefined}
        value={value}
        onChange={onChange}
        className={className}
        {...props}
      />
    );
  }
);
SearchInput.displayName = 'SearchInput';
